import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import createViewer from './viewer/index.js';
import { loadSensors, createSensorObject } from './sensors/index.js';
import logger from './util/logger.js';

// Minimal initViewer helper: creates a canvas inside `container`, loads a GLB model
// from `modelsPath` (modelId from sensors doc or opts.modelId) and optional sensors
// from `sensorsYamlUrl`. Returns an object with `load()` and `dispose()`.
export default function initViewer(container, opts = {}) {
	let { modelsPath = '/', sensorsYamlUrl = null, sensorsData = null, modelId: optModelId = null, onModelLoaded = () => { }, onSensorClick = null, onHover = null } = opts;

	if (!container) throw new Error('initViewer: container element is required');

	const canvas = document.createElement('canvas');
	canvas.style.width = '100%';
	canvas.style.height = '100%';
	canvas.style.display = 'block';
	container.appendChild(canvas);

	const viewer = createViewer({ canvas });

	// internal pending updates queue -- if updateSensorValues is called before
	// sensors group is present or before sensors are added, store updates here
	// as a merged map (latest-wins per key) and flush once after initial load
	let _pendingSensorUpdates = {};
	let _ready = false;
	const _readyCbs = [];

	// Wire viewer hover & click callbacks to provided handlers (if any)
	try {
		viewer.onHover((h) => {
			try {
				if (typeof onHover === 'function') {
					if (!h) return onHover(null);
					const sensor = h.sensor || null;
					const point = h.point && h.point.x !== undefined ? { x: h.point.x, y: h.point.y, z: h.point.z } : null;
					const payload = sensor ? { id: sensor.id || sensor.uid || sensor.label || null, label: sensor.label || null, x: sensor.x, y: sensor.y, z: sensor.z, event: h.event || null, point } : null;
					onHover(payload);
				}
			} catch (e) { console.warn('initViewer: onHover handler threw', e); }
		});
	} catch (e) { /* ignore if viewer doesn't support onHover */ }

	try {
		viewer.onModelClick((s) => {
			try {
				const sensor = s || null;
				const payload = sensor ? { id: sensor.id || sensor.uid || sensor.label || null, label: sensor.label || null, x: sensor.x, y: sensor.y, z: sensor.z, _sensorUid: sensor._sensorUid || null } : null;
				// persist label on click by dispatching label:persistent on the parent element
				try {
					const parent = canvas && canvas.parentElement ? canvas.parentElement : (container && container);
					if (parent && payload && payload._sensorUid) {
						parent.dispatchEvent(new CustomEvent('label:persistent', { detail: { sensorUid: payload._sensorUid, persistent: true } }));
					}
				} catch (ee) { /* ignore persistence dispatch errors */ }
				if (typeof onSensorClick === 'function') {
					try { onSensorClick(payload); } catch (e) { console.warn('initViewer: onSensorClick handler threw', e); }
				}
			} catch (e) { console.warn('initViewer: modelClick wrapper failed', e); }
		});
	} catch (e) { /* ignore if viewer doesn't support clicks */ }

	// Click reporting disabled to avoid pointer-down crash: viewer handles clicks
	// internally but we do not forward them to the host app.

	async function loadSensorsAndModel() {
		try {
			// minimal logging: do not spam debug logs during normal interactions
			let sensors = [];
			let modelId = optModelId || null;

			if (sensorsData) {
				sensors = Array.isArray(sensorsData) ? sensorsData : (sensorsData.sensors || []);
				if (!modelId && sensorsData.model) modelId = sensorsData.model;
			} else if (sensorsYamlUrl) {
				try {
					const resp = await fetch(sensorsYamlUrl);
					if (resp.ok) {
						const txt = await resp.text();
						if (txt && txt.trim()) {
							try {
								const maybeJson = JSON.parse(txt);
								if (Array.isArray(maybeJson)) sensors = maybeJson;
								else { sensors = maybeJson.sensors || []; if (maybeJson.model) modelId = modelId || maybeJson.model; }
							} catch (je) {
								try {
									const jsyaml = await import('js-yaml');
									const doc = jsyaml.load(txt);
									sensors = doc && doc.sensors ? doc.sensors : (Array.isArray(doc) ? doc : []);
									if (doc && doc.model) modelId = modelId || doc.model;
								} catch (ye) { console.warn('Could not parse sensors file as JSON or YAML', ye); }
							}
						}
					}
				} catch (e) { console.warn('Failed to fetch sensors file', e); }
			}

			if (modelId) {
				try {
					let meta = null;
					try {
						const mresp = await fetch(modelsPath + `${modelId}.meta.json`);
						if (mresp.ok) meta = await mresp.json();
					} catch (me) { /* ignore */ }

					const loader = new GLTFLoader();
					const gltf = await loader.loadAsync(modelsPath + `${modelId}.glb`);

					if (meta && Array.isArray(meta.center) && meta.center.length === 3) {
						const c = meta.center; gltf.scene.position.set(-c[0], -c[1], -c[2]);
					}

					viewer.clear();
					viewer.addMesh(gltf.scene);
					// model loaded
					try { onModelLoaded(modelId); } catch (e) { console.warn(e); }
					// If a caller requested to skip the next automatic fit (e.g. when
					// restoring a persisted cameraState), honor that flag once.
					try {
						if (viewer._skipNextFit) {
							viewer._skipNextFit = false;
						} else {
							viewer.fitToScene && viewer.fitToScene();
						}
					} catch (e) { /* ignore fit errors */ }
				} catch (le) { console.warn('Model load failed', le); }
			}

			// add sensors (use sensors module to create sensor objects)
			try {
				const sroot = viewer._getSensors && viewer._getSensors();
				if (sroot) { while (sroot.children.length) sroot.remove(sroot.children[0]); }
			} catch (e) { }

			// adding sensors count: handled silently
			sensors.forEach(s => {
				try {
					const sensorMesh = createSensorObject(s, { radius: 4 });
					viewer.addSensor(sensorMesh);
				} catch (e) { console.warn('Failed to create sensor mesh', e); }
			});
		} catch (err) { console.warn('loadSensorsAndModel error', err); }
	}

	// initial load (keep promise so we can flush pending updates once initial load completes)
	const _initialLoad = loadSensorsAndModel();

	// Expose the underlying viewer API so optional modules (editor, tests)
	// can access internal helpers like `_getSensors()` and camera helpers.
	const api = Object.assign({}, viewer, {
		async load(opts2 = {}) { if (opts2.sensorsData) opts.sensorsData = opts2.sensorsData; if (opts2.sensorsYamlUrl) opts.sensorsYamlUrl = opts2.sensorsYamlUrl; if (opts2.modelsPath) opts.modelsPath = opts2.modelsPath; if (opts2.modelId) opts.modelId = opts2.modelId; await loadSensorsAndModel(); },
		dispose() {
			try { if (viewer && typeof viewer.dispose === 'function') viewer.dispose(); } catch (e) { }
			try { if (canvas && canvas.parentElement) canvas.parentElement.removeChild(canvas); } catch (e) { }
		},
		// Small convenience: compute model center from the internal model object
		getModelCenter() {
			try {
				const m = viewer._getModel && viewer._getModel();
				if (!m) return null;
				const box = new THREE.Box3().setFromObject(m);
				if (box.isEmpty()) return new THREE.Vector3(0, 0, 0);
				return box.getCenter(new THREE.Vector3());
			} catch (e) { return new THREE.Vector3(0, 0, 0); }
		},
		// viewer.focusSensor is provided by the viewer module (uses camera helpers)
	});

	// Proxy a skip-flag onto the underlying viewer so external wrappers
	// (React component) can request the next automatic fit be skipped when
	// restoring a persisted camera. Setting `api._skipNextFit = true` will
	// set `viewer._skipNextFit` which the loader honors above.
	try {
		Object.defineProperty(api, '_skipNextFit', {
			get: function () { return !!viewer._skipNextFit; },
			set: function (v) { try { viewer._skipNextFit = !!v; } catch (e) { /* ignore */ } },
			enumerable: true,
		});
	} catch (e) { /* ignore if defineProperty fails */ }

	// expose small readiness API so callers can wait for viewer to accept updates
	api.onReady = function (cb) {
		if (typeof cb !== 'function') return () => { };
		if (_ready) { try { cb(); } catch (e) { /* ignore */ } return () => { }; }
		_readyCbs.push(cb);
		return () => {
			const i = _readyCbs.indexOf(cb);
			if (i >= 0) _readyCbs.splice(i, 1);
		};
	};

	// incremental sensor values update API
	api.updateSensorValues = function (valuesMap) {
		try {
			logger && logger.debug && logger.debug('updateSensorValues called', { keys: valuesMap ? Object.keys(valuesMap).slice(0, 20) : [] });
			// Console log for cases where logger is disabled
			try { console.log('initViewer: updateSensorValues called', { keys: valuesMap ? Object.keys(valuesMap).slice(0, 20) : [] }); } catch (e) { }
			if (!valuesMap || typeof valuesMap !== 'object') return;
			const sroot = viewer._getSensors && viewer._getSensors();
			// If sensors group not yet available, or present but empty, merge
			// incoming payload into the pending map and return.
			if (!sroot || !Array.isArray(sroot.children) || sroot.children.length === 0) {
				try {
					Object.keys(valuesMap).forEach(k => { _pendingSensorUpdates[k] = valuesMap[k]; });
				} catch (e) { /* ignore */ }
				return;
			}
			const children = sroot.children;

			// helper to apply payload to a target value entry
			function applyPayloadToEntry(entry, payload) {
				if (entry == null) return;
				if (payload !== null && typeof payload === 'object') {
					try { Object.assign(entry, payload); } catch (e) { /* ignore */ }
				} else {
					// primitive payload -> treat as numeric/string value
					entry.value = payload;
				}
			}

			let totalUpdates = 0;
			for (let i = 0; i < children.length; i++) {
				const mesh = children[i];
				if (!mesh || !mesh.userData) continue;
				const sensor = mesh.userData.sensor;
				if (!sensor) continue;

				// If sensor.values exists we support grouped sensors (new format)
				if (sensor.values !== undefined && sensor.values !== null) {
					// build quick lookup for existing value entries
					let valuesLookup = {};
					if (Array.isArray(sensor.values)) {
						for (let vi = 0; vi < sensor.values.length; vi++) {
							const ve = sensor.values[vi];
							if (ve && ve.valueid !== undefined && ve.valueid !== null) valuesLookup[String(ve.valueid)] = ve;
						}
					} else if (typeof sensor.values === 'object') {
						// object-map: keys -> entries
						Object.keys(sensor.values).forEach(k => { valuesLookup[String(k)] = sensor.values[k]; });
					}

					// map incoming keys like "10-1" to the correct sensor by nodeid
					const nodeidStr = sensor.nodeid !== undefined && sensor.nodeid !== null ? String(sensor.nodeid) : null;
					if (!nodeidStr) {
						// fallback to original id-based lookup per-sensor (skip grouped mapping)
						const sid = sensor.id || sensor.clientKey || sensor.label || null;
						const payload = sid ? (valuesMap[sid] || valuesMap[String(sid)]) : null;
						if (payload) {
							try { Object.assign(mesh.userData.sensor, payload); } catch (e) { }
							try { if (typeof mesh.userData._updateLabel === 'function') mesh.userData._updateLabel(mesh.userData.sensor); } catch (e) { }
						}
						continue;
					}

					let updated = false;
					for (const key in valuesMap) {
						if (!Object.prototype.hasOwnProperty.call(valuesMap, key)) continue;
						const m = String(key).match(/^(.+?)[-_:](.+)$/); // accept 10-1 or 10:1 etc.
						if (!m) continue;
						const kNode = m[1];
						const kVal = m[2];
						if (String(kNode) !== nodeidStr) continue;
						const payload = valuesMap[key];

						let target = valuesLookup[String(kVal)];
						if (!target) {
							// create new entry if missing
							const newEntry = {
								valueid: isNaN(Number(kVal)) ? kVal : Number(kVal),
								label: (payload && payload.label) ? payload.label : `value ${kVal}`,
								unit: (payload && payload.unit) ? payload.unit : null,
								value: null,
							};
							// attach into sensor.values (array or object)
							if (Array.isArray(sensor.values)) {
								sensor.values.push(newEntry);
							} else if (typeof sensor.values === 'object') {
								sensor.values[String(kVal)] = newEntry;
							}
							target = newEntry;
							valuesLookup[String(kVal)] = target;
						}
						applyPayloadToEntry(target, payload);
						updated = true;
						totalUpdates++;
					}

					if (updated) {
						try { if (typeof mesh.userData._updateLabel === 'function') mesh.userData._updateLabel(mesh.userData.sensor); } catch (e) { }
					}
				} else {
					// Backwards-compatibility: flat sensor object
					const sid = sensor.id || sensor.clientKey || sensor.label || null;
					if (!sid) continue;
					const payload = valuesMap[sid] || valuesMap[String(sid)];
					if (!payload) continue;
					try { mesh.userData.sensor = Object.assign({}, mesh.userData.sensor, payload); } catch (e) { /* ignore */ }
					try { if (typeof mesh.userData._updateLabel === 'function') mesh.userData._updateLabel(mesh.userData.sensor); } catch (e) { }
					totalUpdates++;
				}
			}
			logger && logger.debug && logger.debug('updateSensorValues applied, totalUpdates=%d', totalUpdates);
			try { console.log('initViewer: updateSensorValues applied', { totalUpdates }); } catch (e) { }

			// If we attempted to apply updates but no children matched (totalUpdates===0),
			// re-queue this payload as a fallback so it gets retried after initial load.
			try {
				if (totalUpdates === 0 && valuesMap && typeof valuesMap === 'object') {
					Object.keys(valuesMap).forEach(k => { _pendingSensorUpdates[k] = valuesMap[k]; });
				}
			} catch (e) { /* ignore */ }
		} catch (e) { console.warn && console.warn('updateSensorValues failed', e); }
	};


	// After initial load completes, mark ready and flush any queued sensor updates
	try {
		if (_initialLoad && typeof _initialLoad.then === 'function') {
			_initialLoad.then(() => {
				_ready = true;
				// call ready callbacks
				try { _readyCbs.forEach(cb => { try { cb(); } catch (e) { /* ignore */ } }); } catch (e) { }
				// flush pending updates (merge map -> single apply, then clear)
				try {
					const keys = Object.keys(_pendingSensorUpdates || {});
					if (keys.length) {
						const merged = Object.assign({}, _pendingSensorUpdates);
						_pendingSensorUpdates = {};
						try { api.updateSensorValues(merged); } catch (e) { /* ignore */ }
					}
				} catch (e) { /* ignore */ }
			}).catch(() => { /* ignore */ });
		}
	} catch (e) { /* ignore */ }

	// expose DOM references for optional UI helpers
	api._container = container;
	api._canvasParent = canvas && canvas.parentElement ? canvas.parentElement : null;

	return api;
}
